import React from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useParams } from "react-router-dom";
import { useGetProjectMembersQuery } from "../../../../../api";
import { MAX_STEP_RESPONSIBLES } from "./helpers/actionPlanConstants";

export type ResponsibleOption = { id: number; label: string }

interface ResponsibleAutocompleteProps {
    value: ResponsibleOption[];
    onChange: (value: ResponsibleOption[]) => void;
    onBlur?: () => void;
    placeholder?: string;
    fullWidth?: boolean;
    size?: "small" | "medium";
    error?: boolean;
    helperText?: React.ReactNode;
}

export const ResponsibleAutocomplete = React.forwardRef<HTMLInputElement, ResponsibleAutocompleteProps>(({
    value,
    onChange,
    onBlur,
    placeholder = "Выберите ответственных",
    fullWidth = true,
    size = "medium",
    error,
    helperText,
}, ref) => {
    const { id } = useParams<{ id: string }>()
    const [search, setSearch] = React.useState("")
    const { data: members = [], isFetching } = useGetProjectMembersQuery(
        { projectId: Number(id) },
        { skip: !id || Number.isNaN(Number(id)) }
    )

    const normalizedSearch = search.trim().toLowerCase()
    const shouldFilter = normalizedSearch.length >= 2
    const options: ResponsibleOption[] = (members as any[])
        .map((m: any) => ({
            id: Number(m?.user?.id ?? m?.id),
            label: String(m?.user?.shortname ?? m?.user?.fullname ?? m?.user?.slug ?? m?.shortname ?? m?.fullname ?? m?.slug ?? "").trim(),
        }))
        .filter((x) => x.id && x.label)
        .filter((x) => !shouldFilter || x.label.toLowerCase().includes(normalizedSearch))

    return (
        <Autocomplete<ResponsibleOption, true, false, false>
            multiple
            disableCloseOnSelect
            size={size}
            options={options}
            noOptionsText="Участник не найден"
            value={value}
            onChange={(_, newValue) => {
                onChange(newValue.slice(0, MAX_STEP_RESPONSIBLES))
            }}
            onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                    setSearch(newInputValue)
                }
            }}
            onBlur={onBlur}
            fullWidth={fullWidth}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputRef={ref}
                    placeholder={placeholder}
                    error={Boolean(error)}
                    helperText={helperText ?? null}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
});

ResponsibleAutocomplete.displayName = "ResponsibleAutocomplete";
